import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '../../lib/queryKeys';
import { apiResponse } from './apiFetch';
import type { AuditLogListResponse } from '@signalvote/shared';
import type { AuditLogListParams } from '@signalvote/shared';

export interface EntityAuditLogsParams {
  page?: number;
  pageSize?: number;
}

export function useFinanceAuditLogs(params: AuditLogListParams) {
  const { page = 1, pageSize = 20, entityType, action, dateFrom, dateTo } = params;

  return useQuery({
    queryKey: queryKeys.finance.auditLogs.list(params),
    queryFn: async () => {
      const searchParams = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
      });

      if (entityType) searchParams.append('entityType', entityType);
      if (action) searchParams.append('action', action);
      if (dateFrom) searchParams.append('dateFrom', dateFrom);
      if (dateTo) searchParams.append('dateTo', dateTo);

      // Status-text errors never read the body, so this checks `ok` itself.
      const response = await apiResponse(`/api/finance/audit-logs?${searchParams.toString()}`);

      if (!response.ok) {
        throw new Error(`Failed to fetch audit logs: ${response.statusText}`);
      }

      return (await response.json()) as AuditLogListResponse;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useEntityAuditLogs(
  entityType: string,
  entityId: string,
  options?: EntityAuditLogsParams
) {
  const { page = 1, pageSize = 20 } = options || {};

  return useQuery({
    queryKey: queryKeys.finance.auditLogs.list({ page, pageSize, entityType, entityId }),
    queryFn: async () => {
      const searchParams = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
        entityType,
        entityId,
      });

      const response = await apiResponse(`/api/finance/audit-logs?${searchParams.toString()}`);

      if (!response.ok) {
        throw new Error(`Failed to fetch entity audit logs: ${response.statusText}`);
      }

      return (await response.json()) as AuditLogListResponse;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useAuditLogEntityTypes() {
  return useQuery({
    queryKey: queryKeys.finance.auditLogs.entityTypes(),
    queryFn: async () => {
      const response = await apiResponse('/api/finance/audit-logs/entity-types');

      if (!response.ok) {
        throw new Error(`Failed to fetch entity types: ${response.statusText}`);
      }

      return (await response.json()) as string[];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useAuditLogActions() {
  return useQuery({
    queryKey: queryKeys.finance.auditLogs.actions(),
    queryFn: async () => {
      const response = await apiResponse('/api/finance/audit-logs/actions');

      if (!response.ok) {
        throw new Error(`Failed to fetch actions: ${response.statusText}`);
      }

      return (await response.json()) as string[];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}
